import { isEmpty } from '@ember/utils';
import Model, { attr, belongsTo, hasMany, type AsyncBelongsTo, type AsyncHasMany } from '@ember-data/model';
import type BlacklistLocationModel from 'garaje/models/blacklist-location';
import type BlacklistVisitorProfileModel from 'garaje/models/blacklist-visitor-profile';
import type CompanyModel from 'garaje/models/company';
import type LocationModel from 'garaje/models/location';
import type ZoneModel from 'garaje/models/zone';

interface Photo {
  original: string | null;
  thumb: string | null;
  dataUrl: string | ArrayBuffer | null;
}

interface CreatedByContext {
  id: string;
  name: string;
  role_name: string;
}

/** Read-only authz envelope from the BE — the authoritative per-record edit check. */
interface BlacklistFilterPermissions {
  'can-edit'?: boolean;
}

/** Scope derived from which relationships resolve. */
export type BlacklistFilterScope = 'all_locations' | 'specific_locations' | 'zone' | null;

export interface CsvExportOptions {
  includeScope?: boolean;
}

const SCOPE_CSV_LABELS: Record<Exclude<BlacklistFilterScope, null>, string> = {
  all_locations: 'All locations',
  specific_locations: 'Specific locations',
  zone: 'Zone',
};

class BlacklistFilter extends Model {
  @belongsTo('location', { async: true, inverse: null }) declare location: AsyncBelongsTo<LocationModel>;
  @belongsTo('zone', { async: true, inverse: null }) declare property: AsyncBelongsTo<ZoneModel>;

  // `locations` linkage type is `blacklist-locations` (the slimmed BE resource), not `locations`.
  @belongsTo('company', { async: true, inverse: null }) declare company: AsyncBelongsTo<CompanyModel>;
  @hasMany('blacklist-location', { async: true, inverse: null })
  declare locations: AsyncHasMany<BlacklistLocationModel>;

  // Profile link. Wire key `visitor-profile`, linkage type
  // `blacklist-visitor-profiles` (a slimmed profile resource). Mock-first: the BE
  // relationship isn't built yet; only set behind the `vis-profile-blocklist` flag.
  @belongsTo('blacklist-visitor-profile', { async: true, inverse: null })
  declare visitorProfile: AsyncBelongsTo<BlacklistVisitorProfileModel>;

  /** Default null so an absent payload fails closed in `canEdit` (not coerced to false). */
  @attr({ defaultValue: () => null }) declare permissions: BlacklistFilterPermissions | null;

  // Created by info
  @attr('string') declare createdBy: string;
  @attr() declare createdByContext: CreatedByContext | null;
  @attr('date') declare createdAt: Date;

  // Core attributes
  @attr('string') declare description: string;
  @attr('string') declare fullName: string;
  @attr('string') declare reason: string;
  @attr('string') declare keyword: string; // deprecated, still used on blacklist-filter

  /**
   * API semantics: disabled=false means "actively blocked", disabled=true means "unblocked/paused"
   * Use isBlocked computed property for clearer intent in UI code
   */
  @attr('boolean', { defaultValue: false }) declare disabled: boolean;
  @attr('date') declare expiresAt: Date | null;

  // Arrays of Strings
  @attr('array', { defaultValue: () => [] }) declare aliases: string[];
  @attr('array', { defaultValue: () => [] }) declare emails: string[];
  @attr('array', { defaultValue: () => [] }) declare otherKeywords: string[];
  @attr('array', { defaultValue: () => [] }) declare phoneNumbers: string[];

  @attr('immutable', {
    defaultValue: () => ({ original: null, thumb: null, dataUrl: null }),
  })
  declare photo: Photo;

  /**
   * Clearer semantic alternative to the inverted 'disabled' attribute
   * @returns true if person is actively blocked, false if unblocked/paused
   */
  get isBlocked(): boolean {
    return !this.disabled;
  }

  /**
   * Scope derived from resolved relationships (no scope attribute on the wire): `zone` if a property
   * is set, `specific_locations` if `locations` is non-empty, `all_locations` if company-scoped, else
   * null. Reads sync relationship refs so it stays untracked-safe in a getter; the caller must have
   * loaded company/property/locations (the table does so via `include`).
   */
  get scope(): BlacklistFilterScope {
    const context = this as BlacklistFilter;
    if (context.belongsTo('property').id()) return 'zone';
    if (this.locationIds.length > 0) return 'specific_locations';
    if (context.belongsTo('company').id()) return 'all_locations';
    return null;
  }

  /** Resolved ids of the `locations` (blacklist-locations) set. Empty when company-wide or unloaded. */
  get locationIds(): string[] {
    return (this as BlacklistFilter)
      .hasMany('locations')
      .ids()
      .filter((id): id is string => id !== null);
  }

  get locationsCount(): number {
    return this.locationIds.length;
  }

  /** Fails closed: an absent `permissions.can-edit` reads as not-editable. */
  get canEdit(): boolean {
    return this.permissions?.['can-edit'] === true;
  }

  get searchable(): string {
    return `${this.description}~${this.fullName}~${this.reason}~${this.searchableAliases}~${this.searchableEmails}~${this.searchableKeywords}~${this.searchablePhoneNumbers}`;
  }

  get searchableAliases(): string {
    const aliases = this.aliases || [];
    if (isEmpty(aliases)) {
      return '';
    }
    return aliases.reduce((all, one) => `${all}${one}`);
  }

  get searchableEmails(): string {
    const emails = this.emails || [];
    if (isEmpty(emails)) {
      return '';
    }
    return emails.reduce((all, one) => `${all}${one}`);
  }

  get searchableKeywords(): string {
    const otherKeywords = this.otherKeywords || [];
    if (isEmpty(otherKeywords)) {
      return '';
    }
    return otherKeywords.reduce((all, one) => `${all}${one}`);
  }

  get searchablePhoneNumbers(): string {
    const phoneNumbers = this.phoneNumbers || [];
    if (isEmpty(phoneNumbers)) {
      return '';
    }
    return phoneNumbers.reduce((all, one) => `${all}~${one}`);
  }

  toDataForCsvExport(options?: CsvExportOptions): Record<string, string> {
    const { id, fullName, description, reason } = this;
    const aliases = this.aliases && this.aliases.length ? this.aliases.join(',') : '';
    const emails = this.emails && this.emails.length ? this.emails.join(',') : '';
    const phoneNumbers = this.phoneNumbers && this.phoneNumbers.length ? this.phoneNumbers.join(',') : '';
    const otherKeywords = this.otherKeywords && this.otherKeywords.length ? this.otherKeywords.join(',') : '';

    return {
      ID: id,
      'Full Name': fullName,
      Aliases: aliases,
      'Email addresses': emails,
      'Phone Numbers': phoneNumbers,
      Keywords: otherKeywords,
      'Physical description': description,
      'Reason for blocking': reason,
      'Expires at': this.expiresAt ? this.expiresAt.toISOString() : '',
      ...(options?.includeScope ? { Locations: this.scope ? SCOPE_CSV_LABELS[this.scope] : '' } : {}),
      Disabled: this.disabled ? 'Yes' : 'No',
    };
  }
}

export default BlacklistFilter;

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'blacklist-filter': BlacklistFilter;
  }
}
